import React from 'react';
import PuzzleButton from '../../UI/button/PuzzleButton';
import PuzzleDropDownButton from '../../UI/button/dropdown/PuzzleDropDownButton';
import { isFunction } from 'lodash';
import { 
  ButtonsPopupSettings, 
  DropDownButtonItemClickEvent
} from '@progress/kendo-react-buttons';
import CellAbstract from './CellAbstract';
import { CELL_ACTIONS_PROPS, ACTION_ITEM } from './Types';

class CellActions extends CellAbstract<CELL_ACTIONS_PROPS, {}> {
  private MAX_VISIBLE_ACTIONS = 2;
  private popupSettings: ButtonsPopupSettings = {
    anchorAlign: {
      horizontal: 'right',
      vertical: 'bottom'
    },
    popupAlign: {
      horizontal: 'right',
      vertical: 'top'
    }
  };

  private isDisabled = (item: ACTION_ITEM, dataItem: any) => isFunction(item.shouldDisable) && item.shouldDisable(dataItem);

  private isHidden = (item: ACTION_ITEM, dataItem: any) => isFunction(item.shouldHide) && item.shouldHide(dataItem);

  private onItemClick = ({ item }: DropDownButtonItemClickEvent) => {
    const { dataItem } = this.props;
    Object.assign(dataItem, item?.actionOptions?.data);
    this.dispatch(item.action, item?.actionOptions?.actionType ? item.actionOptions.actionType : '', {
      dataItem,
      locales: item?.actionOptions?.locales
    });
  };

  /**
   * customRenderer for rendering actions from cell configuration, type needs to be 'custom'
   * @param items
   */
  private renderCustomActions = (items: ACTION_ITEM[]) => {
    const { dataItem } = this.props;
    const actions = items.filter((item: ACTION_ITEM) => !this.isHidden(item, dataItem));

    return <>{actions.map(action => action.customRender?.(dataItem))}</>;
  }

  private renderActions = (items: ACTION_ITEM[]) => {
    const { dataItem } = this.props;
    const actions = items.filter((item: ACTION_ITEM) => !this.isHidden(item, dataItem));

    return (actions.length <= this.MAX_VISIBLE_ACTIONS) ? this.renderInline(actions) : this.renderMenu(actions);
  };

  private renderInline = (items: ACTION_ITEM[]) => (
    <>
      {items.map((item: ACTION_ITEM, index: number) => {
        const { dataItem } = this.props;
        const { text } = item;
        return this.isHidden(item, dataItem) ? null : (
          <PuzzleButton
            disabled={(this.isDisabled(item, dataItem))}
            iconClass={`tp-icon ${item.iconCls}`}
            key={index}
            fillMode="flat"
            onClick={() => {
              Object.assign(dataItem, item?.actionOptions?.data);
              this.dispatch(item.action, item?.actionOptions?.actionType ? item.actionOptions.actionType : '', {
                dataItem, 
                locales: item?.actionOptions?.locales
              });
            }}
            titleText={text}
            tooltipPosition="top"
            withTooltip={Boolean(text)}
          />
        );
      })}
    </>
  );

  private renderMenu = (actionItems: ACTION_ITEM[]) => {
    const { dataItem, cellConfig } = this.props;
    const items = actionItems.reduce((data: any[], item: ACTION_ITEM) => {
      if (!this.isHidden(item, dataItem)) {
        data.push({
          ...item,
          disabled: this.isDisabled(item, dataItem)
        });
      }
      return data;
    }, []);

    const iconCls = cellConfig.iconCls || 'tp-more-vertical k-rotate-90';

    return (
      <PuzzleDropDownButton
        fillMode="flat"
        iconClass={`tp-icon ${iconCls}`}
        items={items}
        onItemClick={this.onItemClick}
        popupSettings={this.popupSettings}
        textField="text"
      />
    );
  };

  render() {
    const {
      items,
      type
    } = this.props.cellConfig;
    let actions;

    switch (type) {
      case 'inline':
        actions = this.renderInline(items);
        break;
      case 'menu':
        actions = this.renderMenu(items);
        break;
      case 'custom':
        actions = this.renderCustomActions(items);
        break;
      default:
        actions = this.renderActions(items);
        break;
    }

    return actions && this.renderCell(actions);
  }
}

export default CellActions;
